<template>
	<div class="revenue-forecast-totals">
		<SuiDataTable v-if="!loading" :col-definition="forecastingTableColumns">
			<SuiDataTableRow>
				<SuiDataTableCell
					class="revenue-forecast-totals__cell"
					:align="SuiDataTableColumnAlign.RIGHT"
				>
					{{ formatAmount(totalEarned) }}
				</SuiDataTableCell>
				<SuiDataTableCell v-if="showBudgetColumn" />
			</SuiDataTableRow>
			<SuiDataTableRow>
				<SuiDataTableCell
					class="revenue-forecast-totals__cell"
					:align="SuiDataTableColumnAlign.RIGHT"
				>
					<span class="revenue-forecast-totals__highlighted">
						{{ formatAmount(totalRecognized) }}
					</span>
				</SuiDataTableCell>
				<SuiDataTableCell v-if="showBudgetColumn" />
			</SuiDataTableRow>
			<SuiDataTableRow>
				<SuiDataTableCell
					class="revenue-forecast-totals__cell"
					:align="SuiDataTableColumnAlign.RIGHT"
				>
					<span class="revenue-forecast-totals__highlighted">
						{{ formatAmount(totalForecasted) }}
					</span>
				</SuiDataTableCell>
				<SuiDataTableCell v-if="showBudgetColumn" />
			</SuiDataTableRow>
			<SuiDataTableRow>
				<SuiDataTableCell
					class="revenue-forecast-totals__cell revenue-forecast-totals__cell--totals"
					:align="SuiDataTableColumnAlign.RIGHT"
				>
					<span
						class="revenue-forecast-totals__highlighted"
						:class="{
							'revenue-forecast-totals__warning': showTotalInRed,
						}"
					>
						{{ formatAmount(totalSum) }}
					</span>
					<div
						v-if="showBudgetColumn && quotedRevenue > 0"
						class="revenue-forecast-totals__light"
						:class="{
							'revenue-forecast-totals__warning': showTotalInRed,
						}"
					>
						{{
							formatNumber((totalSum / quotedRevenue) * 100, {
								decimalPrecision: 0,
							}) + '%'
						}}
					</div>
				</SuiDataTableCell>
				<SuiDataTableCell
					v-if="showBudgetColumn"
					class="revenue-forecast-totals__cell revenue-forecast-totals__cell--totals"
					:align="SuiDataTableColumnAlign.RIGHT"
				>
					<span class="revenue-forecast-totals__highlighted">
						{{ formatAmount(quotedRevenue) }}
					</span>
					<div class="revenue-forecast-totals__light">100%</div>
				</SuiDataTableCell>
			</SuiDataTableRow>
		</SuiDataTable>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
	SuiDataTable,
	SuiDataTableRow,
	SuiDataTableCell,
	type SuiDataTableColumnType,
	SuiDataTableColumnAlign,
} from '@developers/scoro-ui';

import { useProjectRevenueForecast } from './useProjectRevenueForecast';
import { columWidth } from './constants';
import { useFormatter, useTranslation } from '@/composables';
import { type Project, ProjectBudget } from '@/generated/graphql';

export interface ProjectRevenueForecastTotalsProps {
	projectId: Project['id'];
}

const props = defineProps<ProjectRevenueForecastTotalsProps>();

const { t } = useTranslation();
const { formatNumber } = useFormatter();

const {
	loading,
	quotedRevenue,
	totalEarned,
	totalRecognized,
	totalForecasted,
	totalSum,
	formatAmount,
	budgetType,
	usePassThroughCost,
	isFixedFee,
	showBudgetColumn,
} = useProjectRevenueForecast(props.projectId);

// Show red only for fixed_fee projects when total exceeds the budget (including 0 budget).
const showTotalInRed = computed(
	() => isFixedFee.value && totalSum.value > quotedRevenue.value
);

const forecastingTableColumns = computed(() => {
	const cols: SuiDataTableColumnType[] = [
		{
			id: 'total',
			title: t('total'),
			width: columWidth,
			align: SuiDataTableColumnAlign.RIGHT,
		},
	];

	if (showBudgetColumn.value) {
		let titleText =
			budgetType.value === ProjectBudget.Quote
				? t('project_revenue_forecast_quoted')
				: t('project_revenue_forecast_budgeted');
		let titleLabel = '';

		if (!usePassThroughCost.value) {
			titleText += '*';
			titleLabel =
				budgetType.value === ProjectBudget.Quote
					? t('project_revenue_forecast_quoted_tooltip')
					: t('project_revenue_forecast_budgeted_tooltip');
		}

		cols.push({
			id: 'budgeted',
			title: titleText,
			titleLabel,
			width: columWidth,
			align: SuiDataTableColumnAlign.RIGHT,
		});
	}

	return cols;
});
</script>

<style lang="scss">
.revenue-forecast-totals {
	flex-grow: 0;
	flex-shrink: 0;
	margin-left: -1rem;

	.sui-data-table-header__col {
		color: $neutral-90;
		font-weight: $scoro-fw-semi-bold;
		padding-right: 0;
	}

	.revenue-forecast-totals__cell {
		line-height: 4rem;
		padding-right: 0;

		&--totals {
			line-height: 2rem;
			padding-top: 2.5rem;
		}
	}

	&__highlighted {
		color: $neutral-80;
		font-weight: $scoro-fw-semi-bold;
	}

	&__light {
		color: $neutral-70;
		font-size: 1.375rem;
	}

	&__warning {
		color: $red-50;
	}
}
</style>
